import { Component, Inject, WritableSignal, computed, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { firstValueFrom } from 'rxjs';
import { SignatureService, TransactionApiService } from '@gs-2.0/core';
import { environment } from '@environment';

const COPY_FEEDBACK_MS = 2000;
// Query param appended to every generated give URL so contributions made via an
// embedded donate button/link are attributable to this feature in analytics.
const UTM_SOURCE = 'Giving_Link';

export type DonateButtonDesign = 'light' | 'dark';

export interface DonateButtonsDialogData {
  /** Internal fund id (cseg5/fundNumber) the give URL is generated for. */
  fundId: string;
}

/**
 * Dialog opened from the "Donate Buttons" promo card on the fund dashboard.
 * Lets a fund holder pick a donate-button design, auto-generates the matching
 * HTML embed snippet, and generates a unique give (donation) URL for the fund.
 *
 * Figma: https://www.figma.com/design/8lgljs3EwYuC7cHy3ps6xU/GivingSpace---Phase-2?node-id=408-8011
 */
@Component({
  selector: 'app-donate-buttons-dialog',
  standalone: false,
  templateUrl: './donate-buttons-dialog.component.html',
  styleUrl: './donate-buttons-dialog.component.scss',
})
export class DonateButtonsDialogComponent {
  /** Pre-selected by default, per design — the light option is first/default. */
  selectedDesign = signal<DonateButtonDesign>('light');
  codeCopied = signal(false);
  urlCopied = signal(false);

  /**
   * Give (donation) URL for the fund selected in the fund switcher. Starts
   * empty and resolves asynchronously (see buildGiveUrl()), since it comes
   * from a `GET /transaction/generate-donate-link` API call.
   */
  giveUrl = signal('');

  constructor(
    public dialogRef: MatDialogRef<DonateButtonsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DonateButtonsDialogData,
    private clipboard: Clipboard,
    private transactionApiService: TransactionApiService,
    private signatureService: SignatureService
  ) {
    this.buildGiveUrl().then((url) => this.giveUrl.set(url));
  }

  /** Externally-hosted button image for the selected design, used in the embed code's `<img src>`. */
  selectedButtonImageUrl = computed(() =>
    this.selectedDesign() === 'light'
      ? environment.fund.lightDonateNowButtonLink
      : environment.fund.darkDonateNowButtonLink
  );

  /** Recomputes whenever the design or the resolved give URL changes. */
  embedCode = computed(
    () =>
      `<a target="_blank" href="${this.giveUrl()}"><img src="${this.selectedButtonImageUrl()}"></a>`
  );

  onClose(): void {
    this.dialogRef.close();
  }

  selectDesign(design: DonateButtonDesign): void {
    this.selectedDesign.set(design);
  }

  /** Routes the donor to the fund's donation page, matching what the embedded button does. */
  onPreviewClick(): void {
    const url = this.giveUrl();
    if (url && typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  }

  copyEmbedCode(): void {
    this.copy(this.embedCode(), this.codeCopied);
  }

  copyGiveUrl(): void {
    const url = this.giveUrl();
    if (url) {
      this.copy(url, this.urlCopied);
    }
  }

  /**
   * Fetches the fund's give URL from `generate-donate-link`, appends
   * `utm_source`, then signs the result. Works uniformly for Foundation and
   * Charity funds — the endpoint is keyed by fund id, not by an
   * organisation/charity uuid, so there's no per-fund-type fallback needed
   * here (unlike the two prior approaches this replaced — see
   * docs/donate-buttons-dialog/Research.md).
   */
  private async buildGiveUrl(): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.transactionApiService.generateDonateLink(this.data.fundId)
      );
      const url = new URL(response.generatedLink);
      url.searchParams.set('utm_source', UTM_SOURCE);
      return await this.signGiveUrl(url);
    } catch (error) {
      console.error('Failed to generate donate link:', error);
      return '';
    }
  }

  /**
   * Requests a server-generated HMAC signature for the give URL and appends
   * it, mirroring `DonorGivingService.buildSignedUrl()`. The donate form only
   * persists `donationSource` (the `utm_source` on this link) against a
   * transaction when the URL's signature validates, so an unsigned link
   * would silently lose its attribution. Falls back to the unsigned URL if
   * signing fails, so the link stays usable either way.
   */
  private async signGiveUrl(url: URL): Promise<string> {
    try {
      const res = await firstValueFrom(
        this.signatureService.getSignature({ data: url.toString() })
      );
      if (res?.signature) {
        url.searchParams.set('signature', res.signature);
      }
    } catch {
      // fall through — link stays usable, just unsigned
    }
    return url.toString();
  }

  private copy(value: string, flag: WritableSignal<boolean>): void {
    const success = this.clipboard.copy(value);
    if (success) {
      flag.set(true);
      setTimeout(() => flag.set(false), COPY_FEEDBACK_MS);
    }
  }
}
